import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FormField, InlineFeedback, PrimaryButton, SecondaryButton } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useState } from 'react';
import type { MetaConnection } from 'services/api/relifMeta.api';
import { useSubmitEmbeddedSignupCodeMutation } from 'services/api/relifMeta.api';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';
import { RELIF_META_APP_ID, RELIF_META_EMBEDDED_SIGNUP_CONFIG_ID } from 'utils/constants';
import loadFacebookSDK from 'utils/loadFacebookSDK';

interface ConnectionTabProps {
  businessUnitId: number;
  connection: MetaConnection | null;
  connections: MetaConnection[];
  isLoading: boolean;
  selectedConnectionId: number | null;
  onSelectConnection: (id: number) => void;
  onJumpToTab: (key: 'ads' | 'pixel' | 'forms') => void;
}

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const friendlyError = (err: unknown): string => {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data;
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message;
    }
  }
  return 'No pudimos generar la URL de autorización. Intenta nuevamente.';
};

type NextStepKey = 'connect' | 'expired' | 'assign-ads' | 'configure-pixel' | 'configure-events' | 'all-done';

interface NextStep {
  key: NextStepKey;
  eyebrow: string;
  title: string;
  hint: string;
  cta?: { label: string; tab: 'ads' | 'pixel' | 'forms' };
}

const computeNextStep = (connection: MetaConnection | null): NextStep => {
  if (!connection) {
    return {
      key: 'connect',
      eyebrow: 'Paso 1 de 4',
      title: 'Conecta esta unidad de negocio con Meta.',
      hint: 'El primer paso es generar la URL de autorización y elegir el Meta Business correspondiente.',
    };
  }
  if (connection.isExpired) {
    return {
      key: 'expired',
      eyebrow: 'Acción requerida',
      title: 'El token de Meta expiró. Reconecta para retomar la sincronización.',
      hint: 'Mientras el token esté vencido, las cuentas y formularios dejan de sincronizar.',
    };
  }
  const pending = connection.pendingAdAccountsCount ?? 0;
  const assigned = connection.assignedAdAccountsCount ?? 0;
  if (pending > 0 || assigned === 0) {
    return {
      key: 'assign-ads',
      eyebrow: 'Paso 2 de 4',
      title:
        pending > 0
          ? `Asigna ${pending} ${pending === 1 ? 'cuenta pendiente' : 'cuentas pendientes'}.`
          : 'Sincroniza y asigna ad accounts a esta BU.',
      hint: 'Cada cuenta debe quedar vinculada a esta unidad de negocio antes de poder reportar leads.',
      cta: { label: 'Ir a Cuentas', tab: 'ads' },
    };
  }
  const pixels = connection.pixelConfiguredCount ?? 0;
  if (pixels < assigned) {
    return {
      key: 'configure-pixel',
      eyebrow: 'Paso 3 de 4',
      title: `Configura el píxel en ${assigned - pixels} ${assigned - pixels === 1 ? 'cuenta' : 'cuentas'}.`,
      hint: 'Vincula un Meta Pixel a cada ad account para medir conversiones.',
      cta: { label: 'Ir a Píxel & Eventos', tab: 'pixel' },
    };
  }
  const events = connection.eventsConfiguredCount ?? 0;
  if (events < assigned) {
    return {
      key: 'configure-events',
      eyebrow: 'Paso 4 de 4',
      title: `Define el evento de conversión en ${assigned - events} ${assigned - events === 1 ? 'cuenta' : 'cuentas'}.`,
      hint: 'El evento BOFU le permite a Meta optimizar tus campañas.',
      cta: { label: 'Ir a Píxel & Eventos', tab: 'pixel' },
    };
  }
  return {
    key: 'all-done',
    eyebrow: 'Listo',
    title: 'Todo está configurado para esta unidad de negocio.',
    hint: 'Si quieres revisar formularios suscritos, abre la pestaña Formularios.',
    cta: { label: 'Ver Formularios', tab: 'forms' },
  };
};

const ConnectionTab: React.FC<ConnectionTabProps> = ({
  businessUnitId,
  connection,
  connections,
  isLoading,
  selectedConnectionId,
  onSelectConnection,
  onJumpToTab,
}) => {
  const [submitEmbeddedSignupCode, { isLoading: submittingCode }] = useSubmitEmbeddedSignupCodeMutation();
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { notifySaved } = useFeedback();

  const handleConnect = async () => {
    setError(null);
    setAuthLoading(true);

    interface CapturedPostMessage {
      timestamp: number;
      origin: string;
      data: unknown;
      parsed?: unknown;
    }
    const captured: CapturedPostMessage[] = [];
    const messageListener = (event: MessageEvent) => {
      const entry: CapturedPostMessage = {
        timestamp: Date.now(),
        origin: event.origin,
        data: event.data,
      };
      if (typeof event.data === 'string') {
        try {
          entry.parsed = JSON.parse(event.data);
        } catch {
          // not JSON, leave as raw string
        }
      }
      captured.push(entry);
      console.log('[RelifMeta][Browser] postMessage received', entry);
    };
    window.addEventListener('message', messageListener);

    try {
      const FB = await loadFacebookSDK(RELIF_META_APP_ID);
      const fbLoginResponse = await new Promise<Record<string, unknown>>((resolve) => {
        FB.login(
          (response) => {
            console.log('[RelifMeta][Browser] FB.login callback', response);
            resolve(response as unknown as Record<string, unknown>);
          },
          {
            config_id: RELIF_META_EMBEDDED_SIGNUP_CONFIG_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: '',
              sessionInfoVersion: '3',
            },
          }
        );
      });

      const authResponse = fbLoginResponse.authResponse as { code?: string } | undefined;
      const code = authResponse?.code ?? null;

      console.log('[RelifMeta][Browser] flow summary', {
        codePresent: !!code,
        fbLoginResponse,
        capturedMessageCount: captured.length,
        capturedMessages: captured,
      });

      if (!code) {
        setError('La autorización con Meta fue cancelada.');
        return;
      }

      const result = await submitEmbeddedSignupCode({
        code,
        businessUnitId,
        browserDiagnostics: {
          fbLoginResponse,
          postMessages: captured,
        },
      }).unwrap();
      notifySaved(`Conectado a ${result.metaBusinessName}`);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      window.removeEventListener('message', messageListener);
      setAuthLoading(false);
    }
  };

  const isConnecting = authLoading || submittingCode;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: NAVY }} />
      </Box>
    );
  }

  const isExpired = connection?.isExpired === true;
  const isExpiringSoon = connection?.isExpiringSoon === true;

  let statusKey: 'none' | 'expired' | 'expiring' | 'active' = 'none';
  if (connection) {
    if (isExpired) statusKey = 'expired';
    else if (isExpiringSoon) statusKey = 'expiring';
    else if (connection.isActive) statusKey = 'active';
  }
  const STATUS_META: Record<typeof statusKey, { label: string; tone: string; dot: string }> = {
    none: { label: 'Sin conexión', tone: alpha(NAVY, 0.5), dot: alpha(NAVY, 0.35) },
    active: { label: 'Activa', tone: '#1F8A3D', dot: '#53B721' },
    expiring: { label: 'Por expirar', tone: '#A6450B', dot: '#CF5C09' },
    expired: { label: 'Expirada', tone: '#A6450B', dot: '#CF5C09' },
  };
  const status = STATUS_META[statusKey];

  const nextStep = computeNextStep(connection);
  const showConnectionSelector = connections.length > 1;

  return (
    <Stack spacing={3}>
      {error && <InlineFeedback severity="error">{error}</InlineFeedback>}

      {showConnectionSelector && (
        <Box sx={{ maxWidth: 480 }}>
          <FormField
            select
            label={`Esta BU tiene ${connections.length} Meta Business asociados — elige cuál estás viendo`}
            value={String(selectedConnectionId ?? connections[0]?.id ?? '')}
            onChange={(e) => onSelectConnection(Number(e.target.value))}
          >
            {connections.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.metaBusinessName}
                {c.isExpired ? ' · token expirado' : c.isExpiringSoon ? ' · por expirar' : ''}
              </MenuItem>
            ))}
          </FormField>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: { xs: 3, md: 5 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
          gap: { xs: 3, md: 4 },
          alignItems: { md: 'flex-end' },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: status.dot }} />
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: status.tone,
              }}
            >
              {status.label}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: { xs: '2rem', md: '2.75rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              fontWeight: 700,
              color: NAVY,
              wordBreak: 'break-word',
            }}
          >
            {connection?.metaBusinessName ?? 'Conecta esta BU con Meta'}
          </Typography>
          <Typography
            sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.95rem', color: alpha(NAVY, 0.6), maxWidth: 540 }}
          >
            {connection
              ? 'Esta unidad de negocio sincroniza ad accounts, píxel y formularios de leads desde la cuenta de Meta indicada.'
              : 'Genera la URL de autorización para vincular esta unidad de negocio con una cuenta de Meta Business.'}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'row', md: 'column' }} spacing={1.25} sx={{ justifySelf: { md: 'end' } }}>
          {connection ? (
            <SecondaryButton
              onClick={() => {
                handleConnect().catch(() => undefined);
              }}
              disabled={isConnecting}
            >
              {isConnecting ? 'Conectando…' : 'Reconectar'}
            </SecondaryButton>
          ) : (
            <PrimaryButton
              onClick={() => {
                handleConnect().catch(() => undefined);
              }}
              disabled={isConnecting}
            >
              {isConnecting ? 'Conectando…' : 'Conectar Meta'}
            </PrimaryButton>
          )}
        </Stack>
      </Box>

      <NextStepCard
        nextStep={nextStep}
        onAct={() => {
          if (nextStep.cta) onJumpToTab(nextStep.cta.tab);
          else if (!connection) handleConnect().catch(() => undefined);
          else if (nextStep.key === 'expired') handleConnect().catch(() => undefined);
        }}
      />

      {connection && (
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
            p: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: { xs: 2.5, md: 4 },
          }}
        >
          <Field label="Meta Business ID" value={connection.metaBusinessId} mono />
          <Field label="Token expira" value={formatDate(connection.expiresAt)} />
          <Field label="Última sincronización" value={formatDate(connection.lastSyncAt)} />
          <Field
            label="Cuentas asignadas"
            value={`${connection.assignedAdAccountsCount ?? 0}${
              (connection.pendingAdAccountsCount ?? 0) > 0 ? ` (${connection.pendingAdAccountsCount} pendientes)` : ''
            }`}
            emphasis
          />
        </Box>
      )}
    </Stack>
  );
};

const NextStepCard: React.FC<{ nextStep: NextStep; onAct: () => void }> = ({ nextStep, onAct }) => {
  const isDone = nextStep.key === 'all-done';
  const accent = isDone ? '#53B721' : YELLOW;

  return (
    <ButtonBase
      onClick={onAct}
      disabled={!nextStep.cta && nextStep.key !== 'connect' && nextStep.key !== 'expired'}
      sx={{
        textAlign: 'left',
        width: '100%',
        borderRadius: RADIUS_CARD,
        p: { xs: 2.5, md: 3 },
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW,
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover:not(.Mui-disabled)': {
          transform: 'translateY(-2px)',
          boxShadow: '0 16px 40px rgba(21, 24, 58, 0.10)',
        },
        '&.Mui-disabled': { opacity: 1, cursor: 'default' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ width: '100%', alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              width: 4,
              alignSelf: 'stretch',
              borderRadius: 999,
              backgroundColor: accent,
            }}
          />
          <Box>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: alpha(NAVY, 0.55),
                mb: 0.5,
              }}
            >
              {nextStep.eyebrow}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: { xs: '1.0625rem', md: '1.25rem' },
                fontWeight: 700,
                color: NAVY,
                lineHeight: 1.25,
                letterSpacing: '-0.005em',
              }}
            >
              {nextStep.title}
            </Typography>
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.875rem', color: alpha(NAVY, 0.6), mt: 0.5 }}>
              {nextStep.hint}
            </Typography>
          </Box>
        </Stack>
        {(nextStep.cta || nextStep.key === 'connect' || nextStep.key === 'expired') && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignSelf: { xs: 'flex-end', md: 'center' },
              alignItems: 'center',
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
            }}
          >
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY, fontSize: '0.9375rem' }}>
              {nextStep.cta?.label ?? (nextStep.key === 'expired' ? 'Reconectar' : 'Conectar Meta')}
            </Typography>
            <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
          </Stack>
        )}
      </Stack>
    </ButtonBase>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean; emphasis?: boolean }> = ({
  label,
  value,
  mono,
  emphasis,
}) => (
  <Box>
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: alpha(NAVY, 0.5),
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: mono ? '"JetBrains Mono", "SF Mono", monospace' : 'Jost, sans-serif',
        fontSize: emphasis ? '1.5rem' : '0.95rem',
        fontWeight: emphasis ? 700 : 500,
        color: NAVY,
        lineHeight: 1.2,
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ConnectionTab;
